import type {
  HashMismatchError,
  DecompressionError,
  NetworkError,
  HashingError,
  NoConflictError,
  ResetConflictError,
} from './error';
import type {
  Dir,
  WriteFileError,
  StatObjErr,
  GetDirError,
  ReadFileError,
  StatResult,
  CdError,
  DeleteFileError,
} from './fs/types';
import { OptTuple } from './typeUtils';

export type GetCommitError = NetworkError; // malformed request / response
export type CheckFileError =
  | DecompressionError
  | HashMismatchError
  | HashingError
  | StatObjErr; // | HashingError;
export type TreeNodeError = WriteFileError | CheckFileError;
export type FetchError =
  | TreeNodeError
  | GetDirError
  | ReadFileError
  | GetCommitError;
export type WriteObjectError = WriteFileError | GetDirError;
export type ReadObjectError =
  | DecompressionError
  | HashMismatchError
  | HashingError
  | GetDirError
  | ReadFileError;
export type MergeError =
  | GetDirError
  | ReadFileError
  | ReadObjectError
  | WriteFileError;
export type RestoreFileError = ReadObjectError | WriteFileError;

export type ResetError =
  | CdError
  | DeleteFileError
  | RestoreFileError
  | ResetConflictError;
export type ResolveError = ResetError | NoConflictError;

export interface ProjectConfig {
  projectKey: string;
}
export interface Project {
  config: ProjectConfig;
  projectDir: Dir;
  gutDir: Dir;
  objectsDir: Dir;
  getHead: (filename?: string) => Promise<OptTuple<string, ReadFileError>>;
  writeHead: (
    hash: string,
    filename?: string,
  ) => Promise<OptTuple<StatResult, WriteFileError>>;
}

export interface Node {
  hash: string;
  path: string;
  mode?: string;
}
export type BlobNode = Node;
export interface TreeNode extends Node {
  tree: (TreeNode | BlobNode)[];
}

export function isBlobNode(node: TreeNode | BlobNode): node is BlobNode {
  return node.mode !== '40000' && !(node as TreeNode).tree;
}

export function isTreeNode(node: TreeNode | BlobNode): node is TreeNode {
  return !isBlobNode(node);
}

export interface Commit {
  hash: string;
  message: string;
  author: string;
  date: string;
  committer?: string;
  committerWhen?: string;
  parents: string[];
  tree: TreeNode;
  history: Commit[];
}

export enum SyncStateEnum {
  NotSynced = 0, // assumed in progress - we should check at startup in case the app crashed
  SyncSucceeded = 1, // downloading and writing to disk succeeded
  SyncFailed = 2, // failed to download or write to disk
}

export enum StatusType {
  NoChange = 'NoChange',
  Added = 'Added',
  Modified = 'Modified',
  Deleted = 'Deleted',
  Conflicted = 'Conflicted',
  // Moved = 'moved'
}

export enum ConflictStatusEnum {
  NoConflict = 0,
  Base = 1,
  Theirs = 2,
}
export interface SyncStatus {
  sync: boolean;
  state: SyncStateEnum;
  syncError: number; // a number between 0 and 31
}

export interface FileMeta {
  size: number;
  mtime: number;
  hash: string | null;
}

export enum IndexEntryType {
  Head = 'Head',
  Staged = 'Staged',
  BaseConflict = 'BaseConflict',
  OldHeadOnly = 'OldHeadOnly',
}
export interface IndexEntryBase {
  hash: string;
  path: string;
  size: number;
  mtime: number;
  syncStatus: SyncStatus;
  isStaged: boolean;
  isOldHead: boolean;
  conflictStatus: ConflictStatusEnum;
  type?: IndexEntryType;
}

// There can only be one/zero Head entries and either NoConflict or Theirs
export interface HeadIxEntry extends IndexEntryBase {
  type: IndexEntryType.Head;
  isStaged: false;
  isOldHead: false;
  conflictStatus: Exclude<ConflictStatusEnum, ConflictStatusEnum.Base>;
}

// There can be 0-1 Staged entries and they either have NoConflict.
// Can be isStaged or isOldHead. Normally that's two separate entries but
// if the hash happens to be the same then it's the same entry.

// If it is staged it should not be in conflict. We always unstage before merging.
export interface StageIxEntry extends IndexEntryBase {
  type: IndexEntryType.Staged;
  isStaged: true;
  isOldHead: boolean;
  conflictStatus: ConflictStatusEnum.NoConflict;
}
// There can be 0-1 extra entries which are not staged and not head.
// For each entry either, conflictStatus is 'base' or isOldHead is true or both.
export interface BaseConflictIxEntry extends IndexEntryBase {
  type: IndexEntryType.BaseConflict;
  isStaged: false;
  isOldHead: boolean;
  conflictStatus: ConflictStatusEnum.Base;
}

// Entry with isOldHead as true
export interface OldHeadOnlyIxEntry extends IndexEntryBase {
  type: IndexEntryType.OldHeadOnly;
  isStaged: false;
  isOldHead: true;
  conflictStatus: ConflictStatusEnum.NoConflict;
}

// if there is a HeadIxEntry which is synced there should not be a No old head entry

export type IndexEntry =
  | HeadIxEntry
  | StageIxEntry
  | BaseConflictIxEntry
  | OldHeadOnlyIxEntry;

export interface Status {
  type: StatusType;
  path: string;
  head?: IndexEntry;
  oldHead?: IndexEntry; // when new Head has not been downloaded yet if we don't delete the old file
  staged?: IndexEntry;
  conflict: {
    base?: IndexEntry;
    theirs?: IndexEntry;
  };
  workingDirMeta?: FileMeta;
}

export type StatusMap = Map<string, Status>;

export interface DownloadProgress {
  initiated: Date; // syncBlobs asked to
  started: Date | null; // semaphore acquired and downloading has started
  totalBytes: number | null;
  downloadedBytes: number;
}

export type DownloadsCache = Map<string, DownloadProgress>; // hash -> progress

export interface DownloadsStatus {
  projectKey: string;
  downloadsCache: DownloadsCache;
}

export interface UploadProgress {
  initiated: Date;
  started: Date | null; // semaphore acquired and downloading has started
  totalBytes: number | null;
  uploadedBytes: number;
}

export type UploadsCache = Map<string, UploadProgress>; // hash -> progress
export interface UploadsStatus {
  projectKey: string;
  uploadsCache: UploadsCache;
}

export type ResetResult = Map<string, ResetError | null>;
export type ResolveResult = Map<string, ResolveError | null>;
